import React, { forwardRef, useId } from 'react';
import { cx } from './cx';

/**
 * Kit "Estação" — console de operações.
 *
 * Regras do kit (valem para todo componente daqui):
 *  - Cor só por token (bg-surface, text-dim, bg-brand, text-ok/warm/risk/dead). Zero hex inline.
 *  - Vocabulário único: `variant` = forma, `tone` = semântica, `size` = densidade.
 *  - Todo componente aceita `className` (o `cx` resolve conflito) e encaminha `ref`.
 *  - Foco sempre visível: nenhum `focus:outline-none` sem anel de foco no lugar.
 */

export type Tone = 'brand' | 'ok' | 'warm' | 'risk' | 'dead' | 'neutral';
export type Size = 'sm' | 'md' | 'lg';

const TEXTO_TOM: Record<Tone, string> = {
  brand: 'text-brand',
  ok: 'text-ok',
  warm: 'text-warm',
  risk: 'text-risk',
  dead: 'text-dead',
  neutral: 'text-text',
};

const FUNDO_TOM: Record<Tone, string> = {
  brand: 'bg-brand',
  ok: 'bg-ok',
  warm: 'bg-warm',
  risk: 'bg-risk',
  dead: 'bg-dead',
  neutral: 'bg-faint',
};

// Anel de foco padrão — o mesmo em todo controle interativo do kit.
export const foco =
  'outline-none focus-visible:ring-2 focus-visible:ring-brand/70 focus-visible:ring-offset-1 focus-visible:ring-offset-bg';

/* ─── Spinner ─────────────────────────────────────────────────────────────── */

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      role="status"
      aria-label="Carregando"
      className={cx(
        'inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent',
        className,
      )}
    />
  );
}

/* ─── Button ──────────────────────────────────────────────────────────────── */

export type ButtonVariant = 'primary' | 'ghost' | 'outline' | 'danger' | 'quiet';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: Size;
  /** Mostra spinner e desabilita — evita duplo envio sem o chamador controlar `disabled`. */
  loading?: boolean;
  /** Botão que é só ícone: exige `aria-label` para ter nome accessível. */
  iconOnly?: boolean;
}

const BOTAO_TAMANHO: Record<Size, string> = {
  sm: 'text-xs px-2.5 py-1.5 rounded-md gap-1.5',
  md: 'text-sm px-4 py-2 gap-2',
  lg: 'text-[0.95rem] px-5 py-2.5 gap-2',
};

const BOTAO_ICONE: Record<Size, string> = {
  sm: 'h-7 w-7 p-0 rounded-md',
  md: 'h-9 w-9 p-0',
  lg: 'h-10 w-10 p-0',
};

const BOTAO_VARIANTE: Record<ButtonVariant, string> = {
  // Primário = TINTA, não glow: uma superfície sólida do acento, sem brilho interno nem halo.
  primary: 'bg-brand text-white hover:bg-brand-deep',
  ghost: 'bg-surface-2 text-text border border-border/60 hover:bg-surface-3',
  outline: 'border border-border-strong text-text hover:bg-surface-2',
  danger: 'bg-ui-err-solid text-white hover:brightness-110',
  quiet: 'text-dim hover:bg-surface-2 hover:text-text',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading, iconOnly, className, children, disabled, type, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cx(
        'inline-flex select-none items-center justify-center rounded-lg font-medium transition-all duration-150',
        'active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100',
        foco,
        iconOnly ? BOTAO_ICONE[size] : BOTAO_TAMANHO[size],
        BOTAO_VARIANTE[variant],
        className,
      )}
      {...props}
    >
      {loading ? <Spinner /> : null}
      {children}
    </button>
  );
});

/* ─── Card ────────────────────────────────────────────────────────────────── */

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Espaçamento interno como token do kit — antes cada chamador escrevia "p-4" na mão. */
  padding?: 'none' | 'sm' | 'md' | 'lg';
  hover?: boolean;
  /** Substitui o conteúdo por um esqueleto da mesma altura (não pisca layout). */
  loading?: boolean;
  loadingHeight?: string;
}

const CARD_PAD = { none: '', sm: 'p-3', md: 'p-4', lg: 'p-6' } as const;

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { padding = 'none', hover, loading, loadingHeight = 'h-28', className, children, ...props },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cx(
        // Sem sombra e sem borda-de-luz: no plano, a profundidade é a HAIRLINE (ADR-009). Sombra só
        // em overlay — card com elevação no fundo é o que dava a cara de "template com card flutuante".
        'rounded-lg border border-border bg-surface',
        hover && 'transition-shadow hover:shadow-elev-2',
        CARD_PAD[padding],
        loading && loadingHeight,
        className,
      )}
      {...props}
    >
      {loading ? <span className="block h-full w-full animate-pulse rounded-lg bg-surface-2" /> : children}
    </div>
  );
});

export interface CardHeaderProps {
  icon?: React.ComponentType<{ className?: string }>;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  right?: React.ReactNode;
  className?: string;
}

export function CardHeader({ icon: Icon, title, subtitle, right, className }: CardHeaderProps) {
  return (
    <div className={cx('flex items-center justify-between gap-3 border-b border-border px-4 py-3', className)}>
      <div className="flex min-w-0 items-center gap-2.5">
        {/* Ícone de seção é MOBÍLIA, não sinal: fora do orçamento do acento (ADR-009). */}
        {Icon ? (
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-md border border-border bg-surface-2 text-dim">
            <Icon className="h-4 w-4" />
          </span>
        ) : null}
        <div className="min-w-0">
          <div className="truncate font-display text-[0.95rem] font-semibold text-text">{title}</div>
          {subtitle ? <div className="truncate text-xs text-dim">{subtitle}</div> : null}
        </div>
      </div>
      {right}
    </div>
  );
}

/* ─── Field + controles de formulário ─────────────────────────────────────── */

export interface EloCampo {
  id: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
}

export interface FieldProps {
  label?: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  required?: boolean;
  className?: string;
  /**
   * O controle. Duas formas:
   *  - elemento simples — os elos (id/aria) são injetados nele automaticamente;
   *  - função — recebe os elos, para quando o controle está aninhado ou há mais de um.
   */
  children: React.ReactNode | ((elo: EloCampo) => React.ReactNode);
}

/**
 * O Field antigo embrulhava o controle num <label> sem expor id: o erro virava texto solto (leitor
 * de tela não associava ao campo) e a borda do controle não mudava. Agora rótulo, dica e erro ficam
 * ligados ao controle de verdade — via `htmlFor`/`aria-describedby`/`aria-invalid`.
 */
export function Field({ label, hint, error, required, className, children }: FieldProps) {
  const base = useId();
  const id = `${base}-controle`;
  const idAjuda = hint ? `${base}-ajuda` : undefined;
  const idErro = error ? `${base}-erro` : undefined;
  const descrito = [idErro, idAjuda].filter(Boolean).join(' ') || undefined;
  const elo: EloCampo = { id, 'aria-describedby': descrito, 'aria-invalid': error ? true : undefined };

  // Elemento simples recebe os elos por clonagem; o `id` que o chamador já definiu tem prioridade.
  const controle =
    typeof children === 'function'
      ? (children as (e: EloCampo) => React.ReactNode)(elo)
      : React.isValidElement(children)
        ? React.cloneElement(children, { ...elo, ...(children.props as object) } as never)
        : children;

  return (
    <div className={cx('block', className)}>
      {label ? (
        <label htmlFor={id} className="mb-1.5 block text-[0.8rem] font-medium text-text">
          {label}
          {/* ui-err, não sig-dead: "campo obrigatório" não pode competir com "chip queimado". */}
          {required ? <span className="ml-0.5 text-ui-err">*</span> : null}
        </label>
      ) : null}

      {controle}

      {error ? (
        <span id={idErro} role="alert" className="mt-1 block text-xs text-ui-err">
          {error}
        </span>
      ) : hint ? (
        <span id={idAjuda} className="mt-1 block text-xs text-dim">
          {hint}
        </span>
      ) : null}
    </div>
  );
}

const CONTROLE_TAMANHO: Record<Size, string> = {
  sm: 'px-2.5 py-1 text-xs',
  md: 'px-3 py-2 text-sm',
  lg: 'px-3.5 py-2.5 text-[0.95rem]',
};

const controleBase = (size: Size, invalido?: boolean) =>
  cx(
    'w-full rounded-lg border bg-surface-2 text-text transition-colors',
    'placeholder:text-faint',
    'focus:bg-surface focus:outline-none focus:ring-2',
    'disabled:cursor-not-allowed disabled:bg-surface-3 disabled:text-faint',
    invalido
      ? 'border-ui-err/60 focus:border-ui-err focus:ring-ui-err/25'
      : 'border-border focus:border-brand/50 focus:ring-brand/25',
    CONTROLE_TAMANHO[size],
  );

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: Size;
  /** Conteúdo fixo à esquerda/direita (ícone, unidade, "R$", "%"). */
  prefixo?: React.ReactNode;
  sufixo?: React.ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { size = 'md', prefixo, sufixo, className, ...props },
  ref,
) {
  const invalido = props['aria-invalid'] === true;
  const campo = (
    <input
      ref={ref}
      className={cx(controleBase(size, invalido), prefixo && 'pl-9', sufixo && 'pr-9', className)}
      {...props}
    />
  );
  if (!prefixo && !sufixo) return campo;
  return (
    <span className="relative block">
      {prefixo ? (
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-faint">{prefixo}</span>
      ) : null}
      {campo}
      {sufixo ? (
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-faint">{sufixo}</span>
      ) : null}
    </span>
  );
});

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  size?: Size;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { size = 'md', className, ...props },
  ref,
) {
  const invalido = props['aria-invalid'] === true;
  return (
    <textarea ref={ref} className={cx(controleBase(size, invalido), 'resize-y leading-relaxed', className)} {...props} />
  );
});

/** Select nativo do sistema. Para lista longa/buscável use `Combobox`. */
export interface SelectNativeProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  size?: Size;
}

export const SelectNative = forwardRef<HTMLSelectElement, SelectNativeProps>(function SelectNative(
  { size = 'md', className, children, ...props },
  ref,
) {
  const invalido = props['aria-invalid'] === true;
  return (
    <span className="relative block">
      <select
        ref={ref}
        className={cx(controleBase(size, invalido), 'cursor-pointer appearance-none pr-9', className)}
        {...props}
      >
        {children}
      </select>
      {/* Seta desenhada em SVG com currentColor: a versão anterior embutia a cor em hex dentro de
          um data-URI, então ficava invisível no tema claro. */}
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-faint"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
});

/* ─── HealthPip — semáforo de saúde do chip ───────────────────────────────── */

export type EstadoSaude = 'ok' | 'warm' | 'risk' | 'dead' | 'brand' | 'saudavel' | 'aquecendo' | 'em_risco' | 'queimado';

const NORMALIZA: Record<string, Tone> = {
  saudavel: 'ok',
  aquecendo: 'warm',
  em_risco: 'risk',
  queimado: 'dead',
  ok: 'ok',
  warm: 'warm',
  risk: 'risk',
  dead: 'dead',
  brand: 'brand',
};

const BRILHO: Record<Tone, string> = {
  ok: 'shadow-[0_0_0_3px_rgb(var(--sig-ok)/0.18)]',
  warm: 'shadow-[0_0_0_3px_rgb(var(--sig-warm)/0.18)]',
  risk: 'shadow-[0_0_0_3px_rgb(var(--sig-risk)/0.18)]',
  dead: 'shadow-[0_0_0_3px_rgb(var(--sig-dead)/0.18)]',
  brand: 'shadow-[0_0_0_3px_rgb(var(--brand)/0.2)]',
  neutral: '',
};

export function HealthPip({
  estado = 'ok',
  pulse,
  glow,
  className,
}: {
  estado?: EstadoSaude;
  pulse?: boolean;
  glow?: boolean;
  className?: string;
}) {
  const tom = NORMALIZA[estado] ?? 'neutral';
  return (
    <span
      aria-hidden="true"
      className={cx(
        'inline-block h-2.5 w-2.5 rounded-full',
        FUNDO_TOM[tom],
        glow && BRILHO[tom],
        pulse && 'est-pulse',
        className,
      )}
    />
  );
}

/* ─── Badge ───────────────────────────────────────────────────────────────── */

const BADGE_TOM: Record<Tone, string> = {
  ok: 'bg-ok/12 text-ok ring-ok/20',
  warm: 'bg-warm/12 text-warm ring-warm/20',
  risk: 'bg-risk/12 text-risk ring-risk/20',
  dead: 'bg-dead/12 text-dead ring-dead/20',
  // Badge de brand não existe mais (orçamento do acento): 'brand' virou etiqueta neutra.
  brand: 'bg-surface-2 text-dim ring-border',
  neutral: 'bg-surface-2 text-dim ring-border',
};

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone?: Tone | 'saudavel' | 'aquecendo' | 'em_risco' | 'queimado';
  size?: 'sm' | 'md';
}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  { tone = 'neutral', size = 'md', className, children, ...props },
  ref,
) {
  const tom = NORMALIZA[tone] ?? (tone as Tone) ?? 'neutral';
  return (
    <span
      ref={ref}
      className={cx(
        'inline-flex items-center gap-1 rounded-md font-medium ring-1 ring-inset',
        size === 'sm' ? 'px-1.5 py-0 text-[0.7rem]' : 'px-2 py-0.5 text-xs',
        BADGE_TOM[tom] ?? BADGE_TOM.neutral,
        className,
      )}
      {...props}
    >
      {children}
    </span>
  );
});

/* ─── Sparkline ───────────────────────────────────────────────────────────── */

const TRACO_TOM: Record<Tone, string> = {
  brand: 'stroke-brand',
  ok: 'stroke-ok',
  warm: 'stroke-warm',
  risk: 'stroke-risk',
  dead: 'stroke-dead',
  neutral: 'stroke-dim',
};

export function Sparkline({
  data = [],
  width = 72,
  height = 24,
  tone = 'brand',
  className,
}: {
  data?: number[];
  width?: number;
  height?: number;
  tone?: Tone;
  className?: string;
}) {
  if (!data.length) return null;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const amplitude = max - min || 1;
  const xy = (v: number, i: number): [number, number] => [
    (i / (data.length - 1 || 1)) * width,
    height - ((v - min) / amplitude) * height,
  ];
  const pontos = data.map((v, i) => xy(v, i).join(',')).join(' ');
  // Ponto final enfatizado: o "agora" da leitura — o olho acha o valor atual sem ler o eixo.
  const [fx, fy] = xy(data[data.length - 1], data.length - 1);
  return (
    <svg aria-hidden="true" width={width} height={height} viewBox={`-2 -2 ${width + 4} ${height + 4}`} className={cx('overflow-visible', className)} fill="none">
      <polyline points={pontos} className={TRACO_TOM[tone]} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.85" />
      <circle cx={fx} cy={fy} r="2.4" className={TEXTO_TOM[tone]} fill="currentColor" />
    </svg>
  );
}

/* ─── StatTile — instrumento do cockpit ───────────────────────────────────── */

export interface StatTileProps {
  label: React.ReactNode;
  value: React.ReactNode;
  sub?: React.ReactNode;
  tone?: Tone;
  icon?: React.ComponentType<{ className?: string }>;
  delta?: number;
  spark?: number[];
  loading?: boolean;
  className?: string;
}

export function StatTile({ label, value, sub, tone = 'neutral', icon: Icon, delta, spark, loading, className }: StatTileProps) {
  if (loading) {
    return (
      <Card padding="md" className={cx('space-y-3', className)}>
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-8 w-16" />
      </Card>
    );
  }
  // Fallback em runtime: as telas legadas em .js passavam `tone="text"` (tom inexistente) sem erro
  // de tipo (checkJs desligado) e o número saía SEM cor. Nunca renderizar undefined.
  const cor = TEXTO_TOM[tone] ?? TEXTO_TOM.neutral;
  const subiu = typeof delta === 'number' && delta >= 0;
  return (
    <Card padding="md" className={className}>
      <div className="flex items-center justify-between">
        <span className="text-[0.7rem] font-semibold uppercase tracking-wider text-dim">{label}</span>
        {Icon ? <Icon className={cx('h-4 w-4', cor)} /> : null}
      </div>
      <div className="mt-2 flex items-end justify-between gap-2">
        <div className={cx('font-mono-data text-[2rem] font-semibold leading-none', cor)}>{value}</div>
        {spark?.length ? <Sparkline data={spark} tone={tone} className="mb-0.5" /> : null}
      </div>
      {typeof delta === 'number' || sub ? (
        <div className="mt-1.5 flex items-center gap-2">
          {typeof delta === 'number' ? (
            <span
              className={cx(
                'font-mono-data inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[0.68rem] font-semibold',
                subiu ? 'bg-ok/12 text-ok' : 'bg-dead/12 text-dead',
              )}
            >
              {subiu ? '▲' : '▼'} {Math.abs(delta)}%
            </span>
          ) : null}
          {sub ? <span className="text-xs text-dim">{sub}</span> : null}
        </div>
      ) : null}
    </Card>
  );
}

/* ─── Meter ───────────────────────────────────────────────────────────────── */

export function Meter({
  value = 0,
  max = 100,
  tone = 'brand',
  label,
  className,
}: {
  value?: number;
  max?: number;
  tone?: Tone;
  label?: string;
  className?: string;
}) {
  const pct = Math.max(0, Math.min(100, (value / (max || 1)) * 100));
  return (
    <div
      role="progressbar"
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
      className={cx('h-2 w-full overflow-hidden rounded-full bg-surface-2', className)}
    >
      <div className={cx('h-full rounded-full transition-all duration-500', FUNDO_TOM[tone])} style={{ width: `${pct}%` }} />
    </div>
  );
}

/* ─── EmptyState ──────────────────────────────────────────────────────────── */

export function EmptyState({
  icon: Icon,
  title,
  description,
  children,
  action,
  className,
}: {
  icon?: React.ComponentType<{ className?: string }>;
  title?: React.ReactNode;
  /** Alias de `children`. 9 usos em 8 arquivos passavam `description` e o texto sumia (medido 28/09). */
  description?: React.ReactNode;
  children?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}) {
  const texto = children ?? description;
  return (
    <div className={cx('flex flex-col items-center justify-center gap-2 px-6 py-12 text-center', className)}>
      {Icon ? (
        <span className="grid h-12 w-12 place-items-center rounded-xl bg-surface-2 text-faint">
          <Icon className="h-6 w-6" />
        </span>
      ) : null}
      {title ? <div className="font-display text-base font-semibold text-text">{title}</div> : null}
      {texto ? <p className="max-w-sm text-sm text-dim">{texto}</p> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

/* ─── Skeleton ────────────────────────────────────────────────────────────── */

export function Skeleton({ className }: { className?: string }) {
  return <span aria-hidden="true" className={cx('block animate-pulse rounded-md bg-surface-2', className)} />;
}

/* ─── Avatar ──────────────────────────────────────────────────────────────── */

export interface AvatarProps {
  src?: string | null;
  name?: string | null;
  size?: number;
  className?: string;
}

export function Avatar({ src, name, size = 36, className }: AvatarProps) {
  const iniciais = (name || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();

  if (src) {
    return (
      <img
        src={src}
        alt={name || ''}
        width={size}
        height={size}
        loading="lazy"
        className={cx('shrink-0 rounded-full object-cover', className)}
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <span
      aria-hidden={name ? undefined : 'true'}
      title={name || undefined}
      className={cx('grid shrink-0 place-items-center rounded-full bg-surface-3 font-medium text-dim', className)}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.36) }}
    >
      {iniciais}
    </span>
  );
}

export function AvatarGroup({ pessoas, size = 28, max = 4 }: { pessoas: AvatarProps[]; size?: number; max?: number }) {
  const mostra = pessoas.slice(0, max);
  const resto = pessoas.length - mostra.length;
  return (
    <div className="flex items-center">
      {mostra.map((p, i) => (
        <span key={i} className="-ml-2 rounded-full ring-2 ring-surface first:ml-0">
          <Avatar {...p} size={size} />
        </span>
      ))}
      {resto > 0 ? (
        <span
          className="-ml-2 grid place-items-center rounded-full bg-surface-2 font-mono-data text-xs text-dim ring-2 ring-surface"
          style={{ width: size, height: size }}
        >
          +{resto}
        </span>
      ) : null}
    </div>
  );
}

/* ─── Callout — aviso em linha ────────────────────────────────────────────── */

const CALLOUT_TOM: Record<Tone, string> = {
  brand: 'border-brand/40 bg-brand/8',
  ok: 'border-ok/40 bg-ok/8',
  warm: 'border-warm/40 bg-warm/8',
  risk: 'border-risk/40 bg-risk/8',
  dead: 'border-dead/40 bg-dead/8',
  neutral: 'border-border bg-surface-2',
};

export function Callout({
  tone = 'neutral',
  icon: Icon,
  title,
  children,
  action,
  className,
}: {
  tone?: Tone;
  icon?: React.ComponentType<{ className?: string }>;
  title?: React.ReactNode;
  children?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cx('flex items-start gap-3 rounded-lg border px-4 py-3', CALLOUT_TOM[tone], className)}>
      {Icon ? <Icon className={cx('mt-0.5 h-4 w-4 shrink-0', TEXTO_TOM[tone])} /> : null}
      <div className="min-w-0 flex-1">
        {title ? <div className="text-sm font-medium text-text">{title}</div> : null}
        {children ? <div className="text-sm text-dim">{children}</div> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

/* ─── Detalhes tipográficos ───────────────────────────────────────────────── */

/** Tecla de atalho. Usado pela paleta de comandos e por dicas de teclado. */
export function Kbd({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <kbd
      className={cx(
        'inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded border border-border bg-surface-2 px-1',
        'font-mono-data text-[0.7rem] text-dim',
        className,
      )}
    >
      {children}
    </kbd>
  );
}

export function Code({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <code className={cx('rounded bg-surface-2 px-1.5 py-0.5 font-mono-data text-[0.8rem] text-text', className)}>
      {children}
    </code>
  );
}

export function Separator({
  orientation = 'horizontal',
  className,
}: {
  orientation?: 'horizontal' | 'vertical';
  className?: string;
}) {
  return (
    <span
      role="separator"
      aria-orientation={orientation}
      className={cx('block bg-border', orientation === 'vertical' ? 'h-full w-px' : 'h-px w-full', className)}
    />
  );
}
